// src/components/sections/ApiExplanationSection.jsx
import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '../../hooks/useTheme';
import { 
  ArrowRight, 
  Database, 
  Layers, 
  Globe,
  FileCode,
  RefreshCw,
  Zap,
  ShieldCheck
} from 'lucide-react';
import styles from './ApiDocsSection.style';

const ApiDocsSection = () => {
  const { isDarkMode } = useTheme();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }
    }
  };

  // Benefits of using the API
  const apiBenefits = [
    {
      icon: <Layers size={28} />,
      title: "Seamless Integration",
      description: "Connect your systems with our API for frictionless data sharing and workflow automation."
    },
    {
      icon: <Zap size={28} />,
      title: "Real-time Updates",
      description: "Access live data with instant synchronization between your applications and our platform."
    },
    {
      icon: <ShieldCheck size={28} />,
      title: "Enterprise Security",
      description: "Built with bank-grade security standards to ensure your data remains protected."
    },
    {
      icon: <RefreshCw size={28} />,
      title: "Flexible Implementation",
      description: "Use webhooks, REST endpoints, or batch processing based on your business needs."
    }
  ];
  
  return (
    <section 
      id="overview"
      ref={sectionRef} 
      style={styles.section(isDarkMode)}
    >
      {/* Background gradient elements */}
      <div style={styles.backgroundElements}>
        <motion.div
          initial={{ opacity: 0.3 }}
          animate={{ 
            opacity: [0.3, 0.5, 0.3],
            scale: [1, 1.05, 1],
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={styles.gradientBlob1}
        />
        
        <motion.div
          initial={{ opacity: 0.2 }}
          animate={{ 
            opacity: [0.2, 0.4, 0.2],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          style={styles.gradientBlob2}
        />
      </div>

      <div style={styles.container}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          style={styles.contentGrid}
        >
          {/* Main Content - Now Full Width */}
          <motion.div
            variants={itemVariants}
            style={styles.fullWidthContent}
          >
            <div style={styles.featureBadge(isDarkMode)}>
              <div style={styles.badgeIcon}>
                <Database size={12} color="white" />
              </div>
              <span style={styles.badgeText(isDarkMode)}>
                Enterprise API Integration
              </span>
            </div>
            
            <h2 style={styles.mainHeading}>
              Connect and Extend Your ERP with Powerful APIs
            </h2>
            
            <p style={styles.mainDescription(isDarkMode)}>
              Our comprehensive API platform allows developers to seamlessly integrate Daftra's ERP capabilities 
              into their applications. Access real-time data, automate processes, and build 
              custom solutions that connect with your existing business systems.
            </p>
            
            <div style={styles.benefitsGrid}>
              {apiBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.5 + (index * 0.1) }}
                  style={styles.benefitItem}
                >
                  <div style={styles.benefitHeader}>
                    <div style={styles.benefitIconContainer(isDarkMode)}>
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 style={styles.benefitTitle(isDarkMode)}>
                        {benefit.title}
                      </h3>
                      <p style={styles.benefitDescription(isDarkMode)}>
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              variants={itemVariants}
              style={styles.ctaContainer}
            >
              <motion.a
                href="https://cewo6aukdz.apidog.io/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ 
                  scale: 1.03,
                  boxShadow: '0 8px 20px -5px rgba(59, 130, 246, 0.5)'
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  ...styles.primaryButton,
                  background: 'linear-gradient(to right, #3b82f6, #60a5fa)'
                }}
              >
                <FileCode size={18} />
                Explore API Docs
                <ArrowRight size={16} />
              </motion.a>
              
              {/* <motion.div
                whileHover={{ scale: 1.05 }}
                style={styles.secondaryLink}
              >
                <Globe size={20} color={isDarkMode ? '#9cc2ff' : '#3b82f6'} />
                <span style={styles.secondaryLinkText(isDarkMode)}>
                  Get API Key
                </span>
              </motion.div> */}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ApiDocsSection;